/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/ShellAssistantContext.tsx
 * PURPOSE: App-shell Langflow dock state — bottom dock lives outside CRM (Task 11 #92); CRM calls **`openCrmAssistant`** via this provider
 * DEPENDENCIES: react, react-router-dom, ../../features/crm/types/crm.types (**`CrmAssistantState`**, **`CrmAiAssistantPageContext`**)
 * PUBLIC_API: ShellAssistantProvider, useShellAssistant, sessionToCrmAssistantShape, initialDockSession, SetRouteMetaArg
 * SIDE_EFFECTS: React state only
 * AGENT_SAFETY: RESTRICTED
 * FILE_SIZE: 211/220
 *
 * Z-order (documented for #92): CRM entity drawers use z-[100]–z-[128] as overlays above page content.
 * Right assistant rail is in-flow (~z-20) beside main — module drawers still stack above when open.
 * Mobile assistant uses fixed overlay z-[105]/z-[110] (same as shell drawer tokens).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CrmAiAssistantPageContext, CrmAssistantState } from '../../features/crm/types/crm.types';
import type { AssistantStreamRoutingMeta } from '../../hooks/assistant';

/** Optional metadata passed from routes (CRM can extend later via bridge). */
export type AssistantDockRouteContext = {
  route: string;
  selectedRecord?: string | null;
  activeFilters?: Record<string, unknown> | null;
};

export type ShellLangflowSession = {
  /** When true, **`AiAssistantPanel`** is mounted open inside the expanded dock sheet. */
  panelOpen: boolean;
  /** When true, dock shows the expanded bottom sheet; when false, collapsed strip only. */
  dockExpanded: boolean;
  pageContext: CrmAiAssistantPageContext | null;
  prefillContext: string;
  /** When true, dock panel sends **`prefillContext`** immediately after open (brain quick actions). */
  prefillAutoSend: boolean;
  suggestedPrompts: string[];
  prefillNonce: number;
};

export const initialDockSession: ShellLangflowSession = {
  panelOpen: false,
  dockExpanded: false,
  pageContext: null,
  prefillContext: '',
  prefillAutoSend: false,
  suggestedPrompts: [],
  prefillNonce: 0,
};

export function sessionToCrmAssistantShape(s: ShellLangflowSession): CrmAssistantState {
  return {
    open: s.panelOpen && s.dockExpanded,
    prefillContext: s.prefillContext,
    suggestedPrompts: s.suggestedPrompts,
    prefillNonce: s.prefillNonce,
    pageContext: s.pageContext,
  };
}

function blurActiveElementBeforeShellOverlay(): void {
  if (typeof document === 'undefined') return;
  const el = document.activeElement;
  if (el instanceof HTMLElement) el.blur();
}

export type SetRouteMetaArg =
  | AssistantDockRouteContext
  | ((prev: AssistantDockRouteContext) => AssistantDockRouteContext);

type ShellAssistantContextValue = {
  session: ShellLangflowSession;
  routeMeta: AssistantDockRouteContext;
  /** Full replace or functional merge — CRM bridge uses the latter for **`selectedRecord`** only (#92). */
  setRouteMeta: (meta: SetRouteMetaArg) => void;
  openCrmAssistant: (opts: {
    prefillContext: string;
    suggestedPrompts: string[];
    pageContext?: CrmAiAssistantPageContext | null;
  }) => void;
  closeCrmAssistant: () => void;
  /** TopBar / ⌘K — opens dock with route context only. */
  openGlobalAssistant: (
    pageContext: CrmAiAssistantPageContext,
    opts?: { prefillContext?: string; autoSend?: boolean }
  ) => void;
  /** Collapsed-bar chip — opens expanded sheet with one starter string in the composer. */
  openDockWithPromptText: (text: string, pageContext: CrmAiAssistantPageContext) => void;
  /** Collapses the sheet; dock unmounts until the user opens assistant again (TopBar, ⌘K, CRM, or prefill). */
  collapseDock: () => void;
  /** Expands or collapses the sheet chrome (used by chevrons). */
  setDockExpanded: (v: boolean) => void;
  /** Latest topic routing from unified orchestration stream (⌘K dock + panel). */
  orchestrationRouting: AssistantStreamRoutingMeta | null;
  setOrchestrationRouting: (meta: AssistantStreamRoutingMeta | null) => void;
};

/** Nullable for **`CrmProvider`** tests that mount without the app shell. */
export const ShellAssistantReactContext = createContext<ShellAssistantContextValue | null>(null);

export function ShellAssistantProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<ShellLangflowSession>(initialDockSession);
  const [routeMeta, setRouteMetaState] = useState<AssistantDockRouteContext>({
    route: '',
    selectedRecord: null,
    activeFilters: null,
  });
  const [orchestrationRouting, setOrchestrationRouting] =
    useState<AssistantStreamRoutingMeta | null>(null);

  const setRouteMeta = useCallback((meta: SetRouteMetaArg) => {
    setRouteMetaState((prev) => (typeof meta === 'function' ? meta(prev) : meta));
  }, []);

  const openCrmAssistant = useCallback(
    (opts: {
      prefillContext: string;
      suggestedPrompts: string[];
      pageContext?: CrmAiAssistantPageContext | null;
    }) => {
      blurActiveElementBeforeShellOverlay();
      setSession((s) => ({
        panelOpen: true,
        dockExpanded: true,
        prefillContext: opts.prefillContext,
        prefillAutoSend: false,
        suggestedPrompts: opts.suggestedPrompts,
        prefillNonce: s.prefillNonce + 1,
        pageContext: opts.pageContext ?? null,
      }));
    },
    []
  );

  const closeCrmAssistant = useCallback(() => {
    setSession((s) => ({
      ...initialDockSession,
      prefillNonce: s.prefillNonce,
    }));
  }, []);

  const openGlobalAssistant = useCallback(
    (
      pageContext: CrmAiAssistantPageContext,
      opts?: { prefillContext?: string; autoSend?: boolean }
    ) => {
      blurActiveElementBeforeShellOverlay();
      setSession((s) => ({
        panelOpen: true,
        dockExpanded: true,
        pageContext,
        prefillContext: (opts?.prefillContext ?? '').trim(),
        prefillAutoSend: Boolean(opts?.autoSend),
        suggestedPrompts: [],
        prefillNonce: s.prefillNonce + 1,
      }));
    },
    []
  );

  const openDockWithPromptText = useCallback((text: string, pageContext: CrmAiAssistantPageContext) => {
    blurActiveElementBeforeShellOverlay();
    setSession((s) => ({
      panelOpen: true,
      dockExpanded: true,
      pageContext,
      prefillContext: text.trim(),
      prefillAutoSend: false,
      suggestedPrompts: [],
      prefillNonce: s.prefillNonce + 1,
    }));
  }, []);

  const collapseDock = useCallback(() => {
    setSession((s) => ({
      ...s,
      panelOpen: false,
      dockExpanded: false,
    }));
  }, []);

  const setDockExpanded = useCallback((v: boolean) => {
    setSession((s) => ({
      ...s,
      dockExpanded: v,
      panelOpen: v,
    }));
  }, []);

  const value = useMemo(
    () => ({
      session,
      routeMeta,
      setRouteMeta,
      openCrmAssistant,
      closeCrmAssistant,
      openGlobalAssistant,
      openDockWithPromptText,
      collapseDock,
      setDockExpanded,
      orchestrationRouting,
      setOrchestrationRouting,
    }),
    [
      session,
      routeMeta,
      setRouteMeta,
      openCrmAssistant,
      closeCrmAssistant,
      openGlobalAssistant,
      openDockWithPromptText,
      collapseDock,
      setDockExpanded,
      orchestrationRouting,
    ]
  );

  return <ShellAssistantReactContext.Provider value={value}>{children}</ShellAssistantReactContext.Provider>;
}

export function useShellAssistant(): ShellAssistantContextValue {
  const v = useContext(ShellAssistantReactContext);
  if (!v) {
    throw new Error('useShellAssistant must be used within ShellAssistantProvider');
  }
  return v;
}

/** Nullable for **`AiAssistantPanel`** on routes without **`ShellAssistantProvider`**. */
export function useShellAssistantOptional(): ShellAssistantContextValue | null {
  return useContext(ShellAssistantReactContext);
}
